import type { Column, ColumnDef, Row, Table } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LineItem } from "@/apis/gql/types";
import { AccountsCell } from "@/components/shared/cells/accounts";
import { AmountField } from "@/components/shared/cells/amount-cell";
import { BookingTextCell } from "@/components/shared/cells/booking-text";
import { CostCenterCell } from "@/components/shared/cells/cost-center";
import { ExpandRow } from "@/components/shared/cells/expand-row";
import { SelectCell } from "@/components/shared/cells/select";
import { TaxCodeCell } from "@/components/shared/cells/tax-code";
import { VatField } from "@/components/shared/cells/vat-rate";
import type { FormValues as AccountFormValues } from "@/components/shared/form/lineItems/account";
import type { FormValues as CostCenterFormValues } from "@/components/shared/form/lineItems/cost-center";
import type { FormValues as TaxCodeFormValues } from "@/components/shared/form/lineItems/taxCode";
import { ValidationErrorBorder } from "@/components/shared/form/shared/form-validation";
import { SpecialFilterValue } from "@/components/shared/invoices-filters/filter/utils";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import { TooltipContent } from "@/components/ui/tooltip";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AskAi } from "@/features/ask-ai/ask-ai";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { serviceResearchPrompt } from "@/lib/prompts/service";
import { calculateBruttoAmount, calculateNettoAmount, cn } from "@/lib/utils";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetCompanyInfo } from "@/queries/companies-info/fetch";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";
import { InvoiceModes } from "@/types/utils";

const selectColumn = ({
  disabled,
  readOnly,
}: { disabled: boolean; readOnly: boolean }): ColumnDef<any> => ({
  id: "select",
  size: 45,
  header: (header) => (
    <SelectHeader {...header} disabled={disabled} readOnly={readOnly} />
  ),
  cell: (cell) => (
    <div
      className="flex items-center justify-center w-full"
      style={{ paddingLeft: `${cell.row.depth * 2}rem` }}
    >
      <SelectCell {...cell} disabled={disabled} readOnly={readOnly} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
});

const expandColumn: ColumnDef<any> = {
  id: "expand",
  size: 60,
  header: () => null,
  cell: ({ row }) => <ExpandRow row={row} />,
};

const lineItemDescriptionColumn = ({
  handleUpdateLineItems,
  readOnly,
  disabled,
}: {
  handleUpdateLineItems: (
    lineItemIds: string[],
    lineItem: Partial<LineItem>,
  ) => void;
  readOnly: boolean | string | React.ReactNode;
  disabled: boolean | string | React.ReactNode;
}) => ({
  id: "lineItemDescription",
  accessorKey: "description",
  header: ({ column }: { column: Column<any> }) => {
    const { t } = useTranslation();
    return <DataTableColumnHeader column={column} title={t("booking_text")} />;
  },
  cell: ({ row }: { row: Row<any> }) => {
    const description = row.original?.description;
    const invoiceDetails = row.original?.invoice;
    const invoiceText = invoiceDetails?.invoiceText ?? "";
    const invoiceIntent = invoiceDetails?.invoiceIntent ?? "";
    const bookingText = row.original?.bookingText ?? "";

    const handleOnChange = (lineItemIds: string[], text: string) => {
      if (bookingText === text) {
        return;
      }

      handleUpdateLineItems(lineItemIds, {
        bookingText: text,
        bookingTextManuallyUpdated: true,
      });
    };

    return (
      <BookingTextCell
        rowId={row.id}
        subRows={row.subRows}
        depth={row.depth}
        readOnly={readOnly}
        disabled={disabled || row.depth > 0}
        description={description}
        invoiceText={invoiceText}
        invoiceIntent={invoiceIntent}
        className={cn(
          "text-xs",
          Boolean(disabled) && "disabled:opacity-50",
          row.depth > 0 && "disabled:opacity-100",
        )}
        onChange={handleOnChange}
        toolbarDisabled
      />
    );
  },
});

const actionsColumn: ColumnDef<any> = {
  id: "actions",
  header: () => null,
  size: 42,
  // @ts-ignore
  cell: ({ isHovered, table, row }) => {
    const { t } = useTranslation();
    const { companyInfo } = useGetCompanyInfo();

    const description = row.original?.description;
    const invoiceDetails = row.original?.invoice;
    const invoiceText = invoiceDetails?.invoiceText ?? "";

    const showRowActions = isHovered || table.getFocusedRowId() === row.id;

    return (
      <div
        className={`flex space-x-2 transition-opacity duration-200 ${
          showRowActions ? "opacity-100" : "opacity-0"
        }`}
      >
        <TooltipProvider>
          <div className="flex justify-center gap-1">
            <AskAi
              prompt={serviceResearchPrompt(
                description,
                companyInfo?.companyDescription ?? "not available",
                invoiceText,
              )}
              query={description}
              type="internal"
            >
              {({ askAssistant }) => (
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        // this button has priority over anything else.
                        e.stopPropagation();
                        askAssistant();
                      }}
                    >
                      <Search size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("ask_ai.extras.service")}</TooltipContent>
                </Tooltip>
              )}
            </AskAi>
          </div>
        </TooltipProvider>
      </div>
    );
  },
};

const nettoColumn = ({
  invoiceMode,
  readOnly,
  handleUpdateBookingNettoAmount,
  disabled: _disabled,
}: {
  invoiceMode: InvoiceModes;
  readOnly: boolean | string | React.ReactNode;
  handleUpdateBookingNettoAmount: (
    lineItemIds: string[],
    amount: number,
  ) => void;
  disabled: boolean | string | React.ReactNode;
}) => ({
  id: "nettoAmount",
  accessorKey: "nettoAmount",
  size: 80,
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader
      column={column}
      title="Netto"
      buttonClassName={`${
        invoiceMode !== InvoiceModes.Netto ? " text-gray-400" : ""
      }`}
    />
  ),
  cell: ({ row }: { row: Row<any> }) => {
    const _amount = row.original?.amount;

    const amount = calculateNettoAmount(_amount, row.original?.vatRate ?? 0);

    const parentRow = row.getParentRow();

    const lineItemIds = parentRow
      ? [row.original.id]
      : row.original.subRows.map((lineItem: any) => lineItem.id);

    const handleOnBlur = async (value: number) => {
      if (value === amount) {
        return;
      }

      handleUpdateBookingNettoAmount(
        lineItemIds,
        calculateBruttoAmount(value, row.original?.vatRate ?? 0),
      );
    };

    const disabled = useMemo(() => {
      if (_disabled) {
        return true;
      }

      if (parentRow) {
        return true;
      }

      return false;
    }, [parentRow, _disabled]);

    return (
      <AmountField
        key={row.id}
        id={row.id}
        invoiceMode={invoiceMode}
        amount={amount}
        amountConfidence={row.original.amountConfidence}
        fieldType={InvoiceModes.Netto}
        onBlur={handleOnBlur}
        readOnly={readOnly}
        disabled={disabled}
        className="text-xs"
      />
    );
  },
});

const amountColumn = ({
  invoiceMode,
  readOnly,
  handleUpdateBookingAmount,
  disabled: _disabled,
}: {
  invoiceMode: InvoiceModes;
  readOnly: boolean | string | React.ReactNode;
  handleUpdateBookingAmount: (lineItemIds: string[], amount: number) => void;
  disabled: boolean | string | React.ReactNode;
}) => ({
  id: "amount",
  accessorKey: "amount",
  size: 80,
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader
      column={column}
      title="Brutto"
      buttonClassName={`${
        invoiceMode !== InvoiceModes.Brutto ? " text-gray-400" : ""
      }`}
    />
  ),
  cell: ({ row }: { row: Row<any> }) => {
    const amount = row.original?.amount;

    const parentRow = row.getParentRow();

    const lineItemIds = parentRow
      ? [row.original.id]
      : row.original.subRows.map((lineItem: any) => lineItem.id);

    const handleOnBlur = async (value: number) => {
      if (value === amount) {
        return;
      }

      handleUpdateBookingAmount(lineItemIds, value);
    };

    const disabled = useMemo(() => {
      if (_disabled) {
        return true;
      }

      if (parentRow) {
        return true;
      }

      return false;
    }, [parentRow, _disabled]);

    return (
      <AmountField
        key={row.id}
        id={row.id}
        invoiceMode={invoiceMode}
        amount={amount}
        amountConfidence={row.original.amountConfidence}
        fieldType={InvoiceModes.Brutto}
        onBlur={handleOnBlur}
        readOnly={readOnly}
        disabled={disabled}
        className="text-xs"
      />
    );
  },
});

const taxCodeColumn = ({
  invoiceMode,
  handleUpdateTaxCodeId,
  disabled,
  readOnly,
}: {
  invoiceMode: InvoiceModes;
  handleUpdateTaxCodeId: (
    lineItemIds: string[],
    taxCodeId: string | null,
    vatRate: number,
    invoiceMode: InvoiceModes,
  ) => void;
  disabled: boolean | string | React.ReactNode;
  readOnly: boolean | string | React.ReactNode;
}) => ({
  accessorKey: "taxCodeId",
  id: "taxCode",
  size: 150,
  filterFn: (row: Row<any>, columnId: string, value: string) => {
    SpecialFilterValue.Unknown;
    if (!row.original.expenseAccount?.accountId)
      return value === SpecialFilterValue.Unknown;
    else return row.original.expenseAccount?.accountId === value;
  },
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader column={column} title="BU" />
  ),
  sortingFn: (a: Row<any>, b: Row<any>) => {
    const aName = a.original?.taxCode?.datevBu ?? "";
    const bName = b.original?.taxCode?.datevBu ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row }: { row: Row<any>; table: Table<any> }) => {
    const { taxCodes } = useGetAllTaxCodes();
    const parentRow = row.getParentRow();

    const lineItemIds = parentRow
      ? [row.original.id]
      : row.original.subRows.map((lineItem: any) => lineItem.id);

    const accountIds = [row.original.accountId];
    const taxCodeIds = [row.original.taxCodeId];

    const taxCode = taxCodes.find(
      (taxCode) => taxCode.taxCodeId === row.original.taxCodeId,
    );

    const tooltipDescription = [
      {
        name: taxCode?.name ?? "",
        id: taxCode?.datevBu ?? "",
        justification: row.original?.buJustification ?? "",
      },
    ];

    const handleTaxCodeChange = (value: string | null) => {
      const taxCode = taxCodes.find((taxCode) => taxCode.taxCodeId === value);
      const vatRate = taxCode?.vatRate ?? 0;

      handleUpdateTaxCodeId(
        lineItemIds,
        value,
        // TODO: fix this some day
        //@ts-ignore for a reason vatRate is a string. force to be a number
        Number.parseFloat(vatRate),
        invoiceMode,
      );
    };

    const onChange = async (value: TaxCodeFormValues) => {
      handleTaxCodeChange(value.taxCodeId);
    };

    const onSuccess = async (value: string | null) => {
      handleTaxCodeChange(value);
    };

    return (
      <TaxCodeCell
        lineItemIds={lineItemIds}
        accountIds={accountIds}
        taxCodeIds={taxCodeIds}
        onChange={onChange}
        onSuccess={onSuccess}
        tooltipDescriptions={tooltipDescription}
        className={cn(confColor(row.original.taxCodeConfidence))}
        disabled={disabled}
        readOnly={readOnly}
      />
    );
  },
});

const accountColumn = ({
  handleUpdateAccountId,
  invoiceMode,
  disabled,
  readOnly,
}: {
  handleUpdateAccountId: (
    lineItemIds: string[],
    accountId: string | null,
    vatRate: number | undefined,
    invoiceMode: InvoiceModes,
  ) => void;
  invoiceMode: InvoiceModes;
  disabled: boolean | string | React.ReactNode;
  readOnly: boolean | string | React.ReactNode;
}) => ({
  accessorKey: "accountId",
  id: "expenseAccount",
  size: 180,
  filterFn: (row: Row<any>, columnId: string, value: string) => {
    SpecialFilterValue.Unknown;
    if (!row.original.expenseAccount?.accountId)
      return value === SpecialFilterValue.Unknown;
    else return row.original.expenseAccount?.accountId === value;
  },
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader column={column} title="Sachkonto" />
  ),
  sortingFn: (a: Row<any>, b: Row<any>) => {
    const aName = a.original?.expenseAccount?.accountName ?? "";
    const bName = b.original?.expenseAccount?.accountName ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row }: { row: Row<any>; table: Table<any> }) => {
    const { accounts } = useGetAllAccounts();
    const { taxCodes } = useGetAllTaxCodes();

    const parentRow = row.getParentRow();

    const lineItemIds = parentRow
      ? [row.original.id]
      : row.original.subRows.map((lineItem: any) => lineItem.id);

    const accountIds = [row.original.accountId];

    const account = accounts.find(
      (account) => account.accountId === row.original.accountId,
    );

    const tooltipDescription = [
      {
        name: account?.accountName ?? "",
        number: account?.accountNo ?? "",
        justification: row.original?.accountJustification ?? "",
      },
    ];

    const handleUpdateAccount = (value: string | null) => {
      const account = accounts.find((account) => account.accountId === value);

      const taxCodeId = account?.taxCodeId;

      const taxCode = taxCodes.find(
        (taxCode) => taxCode.taxCodeId === taxCodeId,
      );

      const vatRate =
        taxCode?.vatRate != null
          ? //@ts-ignore for a reason vatRate is a string. force to be a number
            Number.parseFloat(taxCode.vatRate)
          : undefined;

      handleUpdateAccountId(lineItemIds, value, vatRate, invoiceMode);
    };

    const onChange = async (value: AccountFormValues) => {
      handleUpdateAccount(value.accountId);
    };

    const onSuccess = (value: string | null) => {
      handleUpdateAccount(value);
    };

    return (
      <ValidationErrorBorder
        name="accountId"
        className="m-1 before:rounded-md before:ring-1 before:ring-destructive"
      >
        <AccountsCell
          lineItemIds={lineItemIds}
          accountIds={accountIds}
          onAccountChange={onChange}
          onSuccess={onSuccess}
          tooltipDescription={tooltipDescription}
          className={cn(confColor(row.original.accountConfidence))}
          disabled={disabled}
          readOnly={readOnly}
        />
      </ValidationErrorBorder>
    );
  },
});

const costCenterColumn = ({
  handleUpdateCostCenterId,
  readOnly,
  disabled,
}: {
  handleUpdateCostCenterId: (
    lineItemIds: string[],
    costCenterId: string | null,
  ) => void;
  readOnly: boolean | string | React.ReactNode;
  disabled: boolean | string | React.ReactNode;
}) => ({
  accessorKey: "costCenterId",
  id: "costCenter",
  size: 200,
  filterFn: (row: Row<any>, columnId: string, value: string) => {
    SpecialFilterValue.Unknown;
    if (!row.original.costCenterId) return value === SpecialFilterValue.Unknown;
    else return row.original.costCenterId === value;
  },
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader column={column} title="Kostenstelle" />
  ),
  sortingFn: (a: Row<any>, b: Row<any>) => {
    const aName = a.original?.costCenter?.name ?? "";
    const bName = b.original?.costCenter?.name ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row }: { row: Row<any> }) => {
    const parentRow = row.getParentRow();

    const lineItemIds = parentRow
      ? [row.original.id]
      : row.original.subRows.map((lineItem: any) => lineItem.id);

    const costCenterIds = [row.original.costCenterId];

    const handleCostCenterChange = (value: string | null) => {
      handleUpdateCostCenterId(lineItemIds, value);
    };

    const onChange = async (value: CostCenterFormValues) => {
      handleCostCenterChange(value.costCenterId);
    };

    const onSuccess = async (value: string | null) => {
      handleCostCenterChange(value);
    };

    return (
      <CostCenterCell
        lineItemIds={lineItemIds}
        costCenterIds={costCenterIds}
        onChange={onChange}
        onSuccess={onSuccess}
        readOnly={readOnly}
        disabled={disabled}
        className={cn(confColor(row.original.costCenterConfidence))}
      />
    );
  },
});

const vatRateColumn = ({
  readOnly,
  handleUpdateBookingVatRate,
  disabled,
}: {
  readOnly: boolean | string | React.ReactNode;
  handleUpdateBookingVatRate: (lineItemIds: string[], vatRate: number) => void;
  disabled: boolean | string | React.ReactNode;
}) => ({
  accessorKey: "vatRate",
  id: "vatRate",
  size: 100,
  enableResizing: true,
  header: ({ column }: { column: Column<any> }) => (
    <DataTableColumnHeader
      column={column}
      title="USt."
      buttonClassName="m-0 p-1"
    />
  ),
  cell: ({ row }: { row: Row<any> }) => {
    const subRows = row.original.subRows ?? [];

    const lineItemIds = subRows.map((lineItem: any) => lineItem.id);

    const handleOnChange = async (value: any) => {
      if (value === row.original.vatRate) {
        return;
      }

      handleUpdateBookingVatRate(lineItemIds, value);
    };

    return (
      <VatField
        key={row.id}
        value={row.original.vatRate}
        confidence={row.original.vatRateConfidence}
        onChange={handleOnChange}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  },
});

const createdAtColumn = {
  accessorKey: "createdAt",
  id: "createdAt",
};

export const columns = ({
  invoiceMode,
  readOnly,
  disabled = false,
  isVorsteuerabzug,
  showCostCenterColumn,
  handleUpdateCostCenterId,
  handleUpdateBookingAmount,
  handleUpdateAccountId,
  handleUpdateTaxCodeId,
  handleUpdateBookingNettoAmount,
  handleUpdateBookingVatRate,
  handleUpdateLineItems,
}: {
  invoiceMode: InvoiceModes;
  readOnly: boolean | string | React.ReactNode;
  disabled: boolean;
  isVorsteuerabzug: boolean;
  showCostCenterColumn: boolean;
  handleUpdateCostCenterId: (
    lineItemIds: string[],
    costCenterId: string | null,
  ) => void;
  handleUpdateBookingAmount: (lineItemIds: string[], amount: number) => void;
  handleUpdateBookingNettoAmount: (
    lineItemIds: string[],
    amount: number,
  ) => void;
  handleUpdateAccountId: (
    lineItemIds: string[],
    accountId: string | null,
    vatRate: number | undefined,
    invoiceMode: InvoiceModes,
  ) => void;
  handleUpdateTaxCodeId: (
    lineItemIds: string[],
    taxCodeId: string | null,
    vatRate: number,
    invoiceMode: InvoiceModes,
  ) => void;
  handleUpdateBookingVatRate: (lineItemIds: string[], vatRate: number) => void;
  handleUpdateLineItems: (
    lineItemIds: string[],
    lineItem: Partial<LineItem>,
  ) => void;
}) => [
  selectColumn({ disabled, readOnly: Boolean(readOnly) }),
  expandColumn,
  lineItemDescriptionColumn({ handleUpdateLineItems, disabled, readOnly }),
  actionsColumn,
  nettoColumn({
    invoiceMode,
    readOnly,
    handleUpdateBookingNettoAmount,
    disabled,
  }),
  amountColumn({ invoiceMode, readOnly, handleUpdateBookingAmount, disabled }),
  ...(!isVorsteuerabzug
    ? [vatRateColumn({ readOnly, handleUpdateBookingVatRate, disabled })]
    : []),
  taxCodeColumn({
    disabled,
    invoiceMode,
    handleUpdateTaxCodeId,
    readOnly,
  }),
  accountColumn({ handleUpdateAccountId, invoiceMode, disabled, readOnly }),
  ...(showCostCenterColumn
    ? [costCenterColumn({ handleUpdateCostCenterId, readOnly, disabled })]
    : []),
  createdAtColumn,
];
